import { END } from 'redux-saga';

export const decorateStore = (store, sagaMiddleware) => {
  /* Internal Methods */
  const runningTasks = new Map();
  const runningTaskRefs = new WeakMap();

  const cleanUpTask = (saga, task) => {
    // Clean up task that finished running.
    // Guard against accidentally removing a newly started task.
    if (task === runningTasks.get(saga)) {
      runningTasks.delete(saga);
    }
  };

  const cancelAllTasks = () => {
    runningTasks.forEach(task => task.cancel());
    runningTasks.clear();
  };

  const createTask = (saga, ...args) => {
    const task = sagaMiddleware.run(saga, ...args);
    task.toPromise().finally(() => cleanUpTask(saga, task));
    runningTasks.set(saga, task);
    runningTaskRefs.set(task, 0);
    return task;
  };

  const incrementRefCount = task => {
    runningTaskRefs.set(task, runningTaskRefs.get(task) + 1);
  };

  const decrementRefCount = task => {
    runningTaskRefs.set(task, runningTaskRefs.get(task) - 1);
  };

  const taskShouldStop = task => runningTaskRefs.get(task) < 1;

  /* External Methods */
  const close = () => store.dispatch(END);

  const runSaga = (saga, ...args) => {
    const task = runningTasks.has(saga) ? runningTasks.get(saga) : createTask(saga, ...args);

    incrementRefCount(task);

    return task;
  };

  const stopSaga = saga => {
    if (runningTasks.has(saga)) {
      const task = runningTasks.get(saga);
      decrementRefCount(task);

      if (taskShouldStop(task)) {
        task.cancel();
        runningTasks.delete(saga);
      }
    }
  };

  return Object.assign(store, {
    close,
    runningTasks,
    runSaga,
    stopSaga,
    cancelAllTasks,
    runSagaOnce: createTask,
  });
};
